import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { logger } from "../utils/logger";
import ValidProductDisplay from "./FullScreenValidProduct";
import MobileValidProduct from "./MobileValidProduct";
import UnverifiedProductDisplay from "./FullScreenUnverifiedProduct";
import NetworkErrorDisplay from "./FullScreenNetworkError";
import MobileNetworkError from "./MobileNetworkError";
import { animationKeyframes } from "./animations";
import MobileUnverifiedProduct from "./MobileUnverifiedProduct";
import ErrorBoundary from "./ErrorBoundary";
import { debounce } from "lodash";
import { getDeviceInfo } from "../utils/deviceInfo";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: white;
  text-align: center;
  --glow-color: #0fa;
  --glow-duration: 0.52s;
  position: relative;
  background: white;
`;

const FadingLogo = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  animation: ${animationKeyframes.fadeInOut} 2s ease-in-out infinite;

  img {
    width: 180px;
    height: auto;
    filter: brightness(0);
  }
`;

const ProductVerification = () => {
  const { code } = useParams();
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isServerError, setIsServerError] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileView, setShowMobileView] = useState(false);
  const [showInitialAnimation, setShowInitialAnimation] = useState(false);

  // Show initial animation immediately on desktop
  useEffect(() => {
    if (!isMobile) {
      setShowInitialAnimation(true);
    }
  }, [isMobile]);

  const handleResize = () => {
    if (window.innerWidth < 768) {
      setShowMobileView(true);
      setIsMobile(true);
    } else {
      setShowMobileView(false);
      setIsMobile(false);
    }
  };

  const debouncedVerify = useCallback(
    debounce(async (code) => {
      try {
        setLoading(true);
        // Start collecting device info
        const deviceInfo = await getDeviceInfo();

        // Make API call with device info
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/verify/${code}`,
          {
            headers: {
              ...deviceInfo,
              "x-user-agent": navigator.userAgent,
              "x-platform": navigator.platform,
              "x-language": navigator.language,
            },
          }
        );

        if (response.status === 429) {
          throw new Error("Please wait a few seconds before verifying again.");
        }
        if (!response.data.success) {
          throw new Error("Verification failed");
        }
        setVerificationResult(response.data);
        setTimeout(() => {
          setShowAnimation(true);
        }, 100);
      } catch (error) {
        console.error("Verification error:", error);
        if (error.response?.status === 429) {
          setError("Please wait a few seconds before verifying again.");
        } else if (error.response?.status === 404) {
          // Handle product not found case
          setVerificationResult({
            success: false,
            data: {
              isValid: false,
              code: code,
              productName: "Unknown Product",
              firstScanDate: new Date().toISOString(),
              scanCount: 0,
            },
          });
          setTimeout(() => {
            setShowAnimation(true);
          }, 100);
        } else {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    }, 2000),
    []
  );

  const handleVerify = async () => {
    try {
      setLoading(true);
      setError(null);
      setVerificationResult(null);

      // Get device information
      const deviceInfo = await getDeviceInfo();

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/verify/${code}`,
        {
          headers: {
            ...deviceInfo,
            "x-user-agent": navigator.userAgent,
            "x-platform": navigator.platform,
            "x-language": navigator.language,
          },
        }
      );

      setVerificationResult(response.data);
    } catch (err) {
      setError(
        err.response?.data?.message || "An error occurred during verification"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const verifyProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsServerError(false);

        // Special case handling for hardcoded product codes
        if (code === "ShowMeValidProduct") {
          const mockData = {
            productName: "Bar Juice 5000",
            firstScanDate: new Date().toISOString(),
            scanCount: 1,
            code: "ShowMeValidProduct",
            isValid: true,
          };

          setVerificationResult({
            success: true,
            data: mockData,
          });
          setTimeout(() => {
            setShowAnimation(true);
          }, 100);
          setLoading(false);
          return;
        }

        if (code === "ShowMeInvalidProduct") {
          const mockData = {
            productName: "Bar Juice 5000",
            firstScanDate: new Date().toISOString(),
            scanCount: 1,
            code: "ShowMeInvalidProduct",
            isValid: false,
          };

          setVerificationResult({
            success: false,
            data: mockData,
          });
          setTimeout(() => {
            setShowAnimation(true);
          }, 100);
          setLoading(false);
          return;
        }

        if (code === "ShowMeNetworkError") {
          setVerificationResult({
            success: false,
            data: {
              code: "ShowMeNetworkError",
              isNetworkError: true,
              scanCount: 1,
              firstScanDate: new Date().toISOString(),
              lastScanDate: new Date().toISOString(),
            },
          });
          setIsServerError(true);
          setError("Server unavailable");
          setTimeout(() => {
            setShowAnimation(true);
          }, 100);
          setLoading(false);
          return;
        }

        // Use the same debounced verification function
        await debouncedVerify(code);
      } catch (err) {
        console.error("Verification error:", err);
        if (err.response) {
          logger.error("Verification failed", {
            status: err.response.status,
            message: err.response.data?.message,
            code: code,
          });

          // Handle rate limiting
          if (err.response.status === 429) {
            setError("Please wait a few seconds before verifying again.");
            return;
          }

          // Only set server error for actual server issues (500, 503)
          if (
            err.response.status === 503 ||
            err.response.status === 500 ||
            (err.response.data?.message &&
              (err.response.data.message.toLowerCase().includes("database") ||
                err.response.data.message
                  .toLowerCase()
                  .includes("initialization")))
          ) {
            setIsServerError(true);
            setError("Server unavailable");
          } else {
            // For 404 or other client errors, treat as invalid product
            setVerificationResult({
              success: false,
              data: {
                isValid: false,
                code: code,
                productName: "Unknown Product",
                firstScanDate: new Date().toISOString(),
                scanCount: 0,
              },
            });
            setTimeout(() => {
              setShowAnimation(true);
            }, 100);
          }
        } else if (err.request) {
          // Network error - no response received
          logger.error("No response received from server", {
            code: code,
          });
          setIsServerError(true);
          setError("Server unavailable");
        } else {
          logger.error("Error setting up verification request", {
            message: err.message,
            code: code,
          });
          setIsServerError(true);
          setError("Server connection error");
        }
        setTimeout(() => {
          setShowAnimation(true);
        }, 100);
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      verifyProduct();
    } else {
      setLoading(false);
      setError("No verification code provided");
    }
  }, [code]);

  if (loading) {
    return (
      <Container>
        <Helmet>
          <title>Product Verification - Bar Juice</title>
          <meta
            name="description"
            content="Verify the authenticity of your Bar Juice product"
          />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <FadingLogo>
          <img src="/images/bj-butterfly.png" alt="Bar Juice Butterfly Logo" />
        </FadingLogo>
      </Container>
    );
  }

  // Show network error display for server errors or network errors
  if ((isServerError && error) || verificationResult?.data?.isNetworkError) {
    return isMobile ? (
      <ErrorBoundary>
        <MobileNetworkError />
      </ErrorBoundary>
    ) : (
      <NetworkErrorDisplay
        verificationResult={
          verificationResult || { data: { isNetworkError: true, code } }
        }
      />
    );
  }

  // Check if the product is valid based on the response
  if (verificationResult?.data?.isValid) {
    if (isMobile) {
      const mobileData = {
        productName: verificationResult.data.productName || "Bar Juice Product",
        productBrand: verificationResult.data.productBrand || "",
        firstScanned: verificationResult.data.firstScanned
          ? new Date(verificationResult.data.firstScanned).toLocaleDateString()
          : new Date().toLocaleDateString(),
        scanCount: verificationResult.data.scanCount || 1,
        code: verificationResult.data.code || code,
        customerId: verificationResult.data.customerId || "0000000",
      };
      return (
        <ErrorBoundary>
          <MobileValidProduct verificationResult={mobileData} />
        </ErrorBoundary>
      );
    } else {
      return <ValidProductDisplay verificationResult={verificationResult} />;
    }
  }

  // Handle unverified product case
  if (isMobile) {
    const mobileData = {
      productName: verificationResult?.data?.productName || "UNKNOWN PRODUCT",
      code: verificationResult?.data?.code || code,
    };
    return (
      <ErrorBoundary>
        <MobileUnverifiedProduct verificationResult={mobileData} />
      </ErrorBoundary>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>Product Verification - Bar Juice</title>
        <meta
          name="description"
          content="Verify the authenticity of your Bar Juice product"
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {showAnimation && (
        <UnverifiedProductDisplay
          verificationResult={
            verificationResult || { data: { isValid: false, code } }
          }
        />
      )}
    </Container>
  );
};

export default ProductVerification;
